import { SupplierStorefrontV2GetResponseType } from "@sellar-io/api-v2/types";
import {
  CalculatedDeliveryOffer,
  calculateDeliveryOffers,
  calculateFulfillmentTypeContainerDeliveryCharge,
  calculateItemTotal,
  calculateTotal,
  calculateVatTotal,
  comparisonFor,
  filterPartiallySatisfiedCaseSizeRules,
  findBiggestApplicableVolumeDiscounts,
  formatCurrency,
  groupItemsByCaseSizeRules,
  haveOrderRulesBeenFulfilled,
} from "@sellar-io/common";
import { OrderSummaryPriceProps } from "../../../components";
import { ContextOrderBasketType } from "../../../hooks";
import {
  createFulfillmentTypeContainerDeliveryChargeParams,
  createOrderCommonParams,
  renderTaxName,
} from "../../../utils/sellarHelpers";
import { isPromotionalOrderItem } from "../categoryPage/Storefront/StorefrontPromotion/isPromotionalOrderItem";

export type CaseSizeRuleType = {
  quantity: number;
  containerType: "can" | "bottle";
  volume: number;
  unit: string;
};

const filterApplicableCaseSizeRules = ({
  caseSizeRuleList,
  orderItemList,
}: {
  caseSizeRuleList: CaseSizeRuleType[];
  orderItemList: {
    containerType: string | null;
    volume: number | null;
    unit: string | null;
  }[];
}) => {
  const applicableCaseSizeRuleSet = new Set<CaseSizeRuleType>();

  for (const item of orderItemList) {
    const ruleFound = caseSizeRuleList.find(
      (rule) =>
        rule.containerType === item.containerType &&
        rule.unit === item.unit &&
        rule.volume === item.volume,
    );

    if (ruleFound) {
      applicableCaseSizeRuleSet.add(ruleFound);
    }
  }

  return [...applicableCaseSizeRuleSet];
};

export const useStockListOrderSummaryProps = ({
  order,
  deliveryChargeRules,
  deliveryCharge,
  deliveryChargeCap,
  fulfillmentTypeContainerDeliveryCharges,
  priceTierDiscounts,
  caseSizeRuleList,
  priceTierOrderRules,
  minimumOrderPrice,
  containerTypeDeliveryChargeRules,
  freeDeliveryBasketSubtotal,
}: {
  order: ContextOrderBasketType | null | undefined;
  deliveryChargeRules?: NonNullable<
    SupplierStorefrontV2GetResponseType["FulfillmentType"]
  >["DeliveryChargeRules"];
  priceTierOrderRules: NonNullable<
    SupplierStorefrontV2GetResponseType["OrderRules"]
  >["PriceTierOrderRules"];
  minimumOrderPrice: NonNullable<
    SupplierStorefrontV2GetResponseType["PriceTier"]
  >["minimumOrderPrice"];
  deliveryCharge?: NonNullable<
    SupplierStorefrontV2GetResponseType["FulfillmentType"]
  >["charge"];
  deliveryChargeCap?: NonNullable<
    SupplierStorefrontV2GetResponseType["FulfillmentType"]
  >["deliveryChargeCap"];
  fulfillmentTypeContainerDeliveryCharges?: NonNullable<
    SupplierStorefrontV2GetResponseType["FulfillmentType"]
  >["FulfillmentTypeContainerDeliveryCharges"];
  priceTierDiscounts: NonNullable<
    SupplierStorefrontV2GetResponseType["VolumeDiscounts"]
  >["PriceTierDiscounts"];
  caseSizeRuleList: SupplierStorefrontV2GetResponseType["CaseSizeRules"]["list"];
  containerTypeDeliveryChargeRules: SupplierStorefrontV2GetResponseType["FulfillmentType"]["ContainerTypeDeliveryChargeRules"];
  freeDeliveryBasketSubtotal?: number | null;
}): OrderSummaryPriceProps & {
  haveMoqRulesBeenSatisfied: boolean;
  hasMinimumOrderPriceBeenMet: boolean;
  orderApplicableCaseSizeRules: CaseSizeRuleType[];
  partiallySatisfiedCaseSizeRules: CaseSizeRuleType[];
  calculatedContainerTypeDeliveryOffers: CalculatedDeliveryOffer[];
  deliveryChargeWithoutRules: number;
} & {
  apiValues: {
    deliveryCharge: number;
    isDeliveryChargeCapped: boolean;
    price: number;
    discountAmount: number;
    subTotal: number;
  };
} => {
  const genericDeliveryCharge = {
    charge: 0,
    isDeliveryChargeCapped: false,
  };
  if (!order?.OrderItems) {
    const calculatedContainerTypeDeliveryOffers =
      containerTypeDeliveryChargeRules
        ? calculateDeliveryOffers(containerTypeDeliveryChargeRules, [])
        : [];

    return {
      calculatedContainerTypeDeliveryOffers,
      orderApplicableCaseSizeRules: [],
      partiallySatisfiedCaseSizeRules: [],
      hasMinimumOrderPriceBeenMet: false,
      haveMoqRulesBeenSatisfied: false,
      deliveryChargeWithoutRules: 0,
      apiValues: {
        ...genericDeliveryCharge,
        deliveryCharge: genericDeliveryCharge.charge,
        price: 0,
        discountAmount: 0,
        subTotal: 0,
      },
      tax: {
        name: "",
        percentage: "",
        total: "",
      },
      discount: undefined,
      total: "",
    };
  }

  const orderItemList = order.OrderItems.filter((orderItem) => {
    return !isPromotionalOrderItem(orderItem);
  }).map((orderItem) => {
    return {
      quantity: orderItem.quantity,
      volume: orderItem.Product.volume ?? null,
      unit: orderItem.Product.unit ?? null,
      packQuantity: orderItem.Product.packQuantity ?? null,
      containerType: orderItem.Product.containerType ?? null,
      price: orderItem.price,
      discountable: orderItem.discountable,
    };
  });

  const calculatedContainerTypeDeliveryOffers = containerTypeDeliveryChargeRules
    ? calculateDeliveryOffers(containerTypeDeliveryChargeRules, orderItemList)
    : [];

  // Note: delivery charge must be 0 and the comparator "greaterThanEqual" otherwise behaviour maybe unpredictable
  const freeDeliveryMet =
    deliveryChargeRules?.[0] &&
    comparisonFor(
      deliveryChargeRules[0].comparator,
      calculateItemTotal(order.OrderItems),
      deliveryChargeRules[0].value || 0,
    );

  const deliveryChargeApiValue = order.OrderItems
    ? fulfillmentTypeContainerDeliveryCharges?.length ||
      deliveryCharge ||
      calculatedContainerTypeDeliveryOffers ||
      freeDeliveryBasketSubtotal
      ? calculateFulfillmentTypeContainerDeliveryCharge({
          ...createFulfillmentTypeContainerDeliveryChargeParams(
            order.OrderItems,
            fulfillmentTypeContainerDeliveryCharges,
            deliveryChargeRules,
            deliveryCharge,
            deliveryChargeCap,
            freeDeliveryBasketSubtotal,
          ),
          calculatedContainerTypeDeliveryOffers,
        })
      : genericDeliveryCharge
    : genericDeliveryCharge;

  // Probably worth discussing this as doesnt feel like the best approach,
  // but this is used to show the buyer what the delivery charge WOULD have been
  const deliveryChargeWithoutRules =
    calculateFulfillmentTypeContainerDeliveryCharge({
      ...createFulfillmentTypeContainerDeliveryChargeParams(
        order.OrderItems,
        fulfillmentTypeContainerDeliveryCharges,
        [],
        deliveryCharge,
      ),
      calculatedContainerTypeDeliveryOffers,
    });

  const biggestApplicableVolumeDiscounts = findBiggestApplicableVolumeDiscounts(
    {
      volumeDiscountList: priceTierDiscounts,
      caseSizeRuleList: caseSizeRuleList,
      orderItemList,
    },
  );

  const discountAmountApiValue = biggestApplicableVolumeDiscounts.reduce(
    (total, discount) => total + discount.discountAmount,
    0,
  );

  const totalDeliveryBeforeDiscount = formatCurrency(
    deliveryChargeApiValue.charge,
    order.currency,
  );
  // Question: why can't we just check for 0 and make that the "FREE" string?
  // Answer: Because sometimes a supplier hasn't set delivery charges but they don't want that to be promoted as "FREE"
  const deliveryChargeString = freeDeliveryMet
    ? "FREE"
    : totalDeliveryBeforeDiscount;

  const discount =
    discountAmountApiValue > 0
      ? {
          calculatedTotal: formatCurrency(
            discountAmountApiValue,
            order.currency,
          ),
          value: formatCurrency(discountAmountApiValue, order.currency),
        }
      : undefined;

  const orderCommon = createOrderCommonParams({
    ...order,
    deliveryCharge: deliveryChargeApiValue.charge,
    discountAmount: discountAmountApiValue,
  });

  const tax = {
    name: renderTaxName(order.currency),
    percentage: order.vatPercentage?.toString() ?? "20",
    total: formatCurrency(calculateVatTotal(orderCommon), order.currency),
  };

  const totalApiValue = calculateTotal(orderCommon);

  const total = formatCurrency(calculateTotal(orderCommon), order.currency);

  const orderApplicableCaseSizeRules = filterApplicableCaseSizeRules({
    caseSizeRuleList: caseSizeRuleList,
    orderItemList,
  });

  const orderItemsGroupedByCaseSizeRules = groupItemsByCaseSizeRules({
    caseSizeRuleList: orderApplicableCaseSizeRules,
    orderItemList,
  });

  const haveMoqRulesBeenSatisfied = haveOrderRulesBeenFulfilled({
    orderItemList: orderItemsGroupedByCaseSizeRules,
    orderRuleList: priceTierOrderRules,
  });

  const hasMinimumOrderPriceBeenMet = totalApiValue >= (minimumOrderPrice ?? 0);

  const partiallySatisfiedCaseSizeRules = filterPartiallySatisfiedCaseSizeRules(
    {
      caseSizeRuleList: orderApplicableCaseSizeRules,
      orderItemList,
    },
  );

  return {
    calculatedContainerTypeDeliveryOffers,
    orderApplicableCaseSizeRules,
    partiallySatisfiedCaseSizeRules,
    haveMoqRulesBeenSatisfied,
    hasMinimumOrderPriceBeenMet,
    deliveryChargeWithoutRules: deliveryChargeWithoutRules.charge,
    subTotal: formatCurrency(
      calculateItemTotal(order.OrderItems),
      order.currency,
    ),
    delivery: deliveryChargeString,
    discount,
    tax,
    total,
    apiValues: {
      isDeliveryChargeCapped: deliveryChargeApiValue.isDeliveryChargeCapped,
      deliveryCharge: deliveryChargeApiValue.charge,
      price: totalApiValue,
      discountAmount: discountAmountApiValue,
      subTotal: calculateItemTotal(order.OrderItems),
    },
  };
};
